import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../environment';
import { PriceCalculatorMapper } from '../price-calculator/price-calculator.mapper';
import { IInitializePriceCalculator } from '../../model/models/price-calculator/initialize-price-calculator.interface';
import { HttpProvider } from '../http/http';
import { IDictionary } from '../../model/models/dictionary.interface';
import { ICountry } from '../../model/models/location/country.interface';
import { DirectBankBrand, PaymentMethodType, PaymentMethodTypeID, PayNearMeBrand, StagedRiaBrand } from '../../model/types/payment-method-type';
import { TranslateService } from '@ngx-translate/core';
import { Country } from '../../model/myria/app/country';
import { regions } from '../../environment/region/regions';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { brandings } from '../../environment/branding/brandings';

@Injectable()
export class ResourceProvider {
  static cleanCache: boolean = false;
  initialized: boolean = false;
  initPromise: Promise<any>;
  initResolve = null;
  requestNr = 0;

  calculator: IInitializePriceCalculator;
  countries: any[];
  sendCountries: any[];
  allCountries: Country[];
  destinationCountries;
  paymentMethods: any[];
  directBankBrand;
  resources;
  useResourcesEndpoint: boolean = true;
  userCountryCode: string = '';

  constructor(private httpProvider: HttpProvider,
              private translate: TranslateService,
              ) {
  }  
  
  initializeResources(forceReload: boolean = false, forceCountryReload = false) {
    // ToDo: remove when all regions use the resources endpoint
    this.useResourcesEndpoint = (APP_CONFIG.branding.branding === brandings.RIA.branding);

    if (this.initialized && !forceReload)
      return;
    else if (this.initPromise && !forceReload) // if a reload is forced, reload it even when in progress because the language may have changed
      return;

    // Two separate API calls are needed to initialize the price calculator, one for countries and certain content, another for the rest of the content.
    // TODO: improve APIs
    const calls = [];
    if (this.useResourcesEndpoint) {
        this.userCountryCode = APP_CONFIG.region.defaultCountryFrom;
        if(ResourceProvider.cleanCache) {
          calls.push(this.httpProvider.get(`${APP_CONFIG.API_URL}api/Resources?CountryId=${APP_CONFIG.region.regionId}&locale=${HttpProvider.currentLanguage}&cleanCache=true`));
        } else {
          calls.push(this.httpProvider.get(`${APP_CONFIG.API_URL}api/Resources?CountryId=${APP_CONFIG.region.regionId}&locale=${HttpProvider.currentLanguage}`));
        }
        calls.push(this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}Calculator/Initialize`));
        calls.push(this.httpProvider.get(`${APP_CONFIG.API_URL}api/Agents/PaymentMethods`)); // !!TODO: Temporary fix until backend fixes the resources endpoint to return payment methods
    } else {
        calls.push(this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}Calculator/Initialize`));
        calls.push(this.httpProvider.get(`${APP_CONFIG.API_URL}api/Agents/PaymentMethods`));
        if (!this.sendCountries || forceCountryReload) {
          calls.push(this.httpProvider.get(`${APP_CONFIG.API_URL}api/OfferedDestinationCountries`));
          calls.push(this.httpProvider.get(`${APP_CONFIG.API_URL}api/Countries?locale=${HttpProvider.currentLanguage}`));
          calls.push(this.httpProvider.get(`${APP_CONFIG.API_URL}api/Customer/NewCustomerRequiredFields`));
        }
    }

    if (!this.initPromise) {
      LoggerProvider.info(this, 'InitializeResources: create initPromise');
      this.initPromise = new Promise((resolve) => {
        if (this.initResolve) {
          // A previous load left a dangling resolver (e.g. it threw mid-processing).
          // Release its waiters and always capture this promise's resolve — otherwise
          // the new promise can never resolve and getResources() hangs forever.
          LoggerProvider.info(this, 'InitializeResources: releasing stale initResolve');
          this.initResolve();
        }
        LoggerProvider.info(this, 'InitializeResources: create initResolve');
        this.initResolve = resolve;
      });
    }

    // ensure that only the latest request is used
    this.requestNr++;
    const requestNr = this.requestNr;
    LoggerProvider.info(this, 'InitializeResources: start ' + requestNr);

    this.httpProvider.multipleCalls(calls, (responses) => {
      if (requestNr !== this.requestNr) {
        LoggerProvider.info(this, 'InitializeResources: outdated request ' + requestNr);
        return true;
      }

      LoggerProvider.info(this, 'InitializeResources: received request ' + requestNr);
      this.initPromise = null;
      try {
        this.initialized = true;

        if (this.useResourcesEndpoint) {
            this.resources = responses[0];
            const countriesResources = this.resources.Countries;

            this.calculator = PriceCalculatorMapper.fromInitializePriceCalculatorResponseEntity(responses[1], countriesResources);
            this.sendCountries = this.calculator.availableSendToCountries;
            // this.paymentMethods = this.resources.PaymentMethods.Value;
            this.paymentMethods = responses[2].Value || []; // !!TODO: Temporary fix until backend fixes the resources endpoint to return payment methods
            this.resources.PaymentMethods = { Value: this.paymentMethods };
        } else {
            if (!this.resources)
              this.resources = {};
            this.calculator = PriceCalculatorMapper.fromInitializePriceCalculatorResponseEntity(responses[0],  responses[3] ? responses[3] : []);
            this.paymentMethods = responses[1].Value || [];
            this.resources.PaymentMethods = { Value: this.paymentMethods };
            this.resources.FavoriteCountries = [];
            this.resources.NewCustomerRequiredFields = responses[4];
            this.sendCountries = this.calculator.availableSendToCountries;
        }

        if(APP_CONFIG.region === regions.US && !APP_CONFIG.featureFlags.kroger && this.paymentMethods.length > 1) {
          this.paymentMethods = this.paymentMethods.filter((paymentMethod) => {
            if (paymentMethod.value === PaymentMethodType.StagedRia)
              return false;
            return true;
          })
        }

        // TODO improve
        for (let i=0; i <this.paymentMethods.length; i++) {
          const method = this.paymentMethods[i];
          const methodObj = this.paymentMethodTextAndLabel(method);
           method.isHidden = false;
          if(method.value === PaymentMethodType.ApplePayCredit || method.value === PaymentMethodType.GooglePayCredit) {
            const appleOrGooglePayIndex = this.paymentMethods.findIndex(obj => (obj.value === PaymentMethodType.GooglePay || obj.value === PaymentMethodType.ApplePay));

            if(appleOrGooglePayIndex > -1) {
              method.isHidden = true;
            }
          }
          if(methodObj.text) {
            method.text = methodObj.text;
          }
          if(methodObj.subTitle) {
            method.subTitle = methodObj.subTitle;
          }
          if (methodObj.showSubTitle) {
            method.showSubTitle = methodObj.showSubTitle;
          }
        
          // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
          if (APP_CONFIG.remittanceApiBaseUrl.indexOf('https://qa09-public.riamoneytransfer.com') === 0) {
            if (method.value === 'CreditCard' || method.value === 'DebitCard')
              method.pspPaymentMethod = 'scheme';
            else if (method.value === 'DirectBank' && APP_CONFIG.region.defaultCountryFrom === 'NL')
              method.pspPaymentMethod = 'ideal';
            if (methodObj.showSubTitle) {
              method.showSubTitle = methodObj.showSubTitle;
            }
          }
        }

        const registrationCountries = [];
        for (const region of APP_CONFIG.regions) {
          registrationCountries.push(region.defaultCountryFrom);
        }

        if (this.useResourcesEndpoint) {
          this.destinationCountries = this.resources.Countries.filter((country) => country.IsOfferedDestinationCountry);

          this.countries = [];
          this.allCountries = this.resources.Countries;
          this.allCountries = this.allCountries.sort(function(a, b) {
            return a.Name.toUpperCase() > b.Name.toUpperCase() ? 1 : b.Name.toUpperCase() > a.Name.toUpperCase() ? -1 : 0;
          });
          for (let i=0; i < this.allCountries.length; i++) {
            const country: any = this.allCountries[i];
            const countryValue = country.NameAbbrev.toUpperCase();

            // IsRegistrationCountry coming from the BE comes from the supported countries endpoint but we don't want to have the same as RMT4
            // we want that RMT5 uses the same registration countries as OneApp hence align it with OneApp registration countries
            country.IsRegistrationCountry = (registrationCountries.indexOf(countryValue) >= 0);

            this.countries.push({ value: countryValue, text: country.Name, countryCode: country.CountryCode.trim(), isRegistrationEnabled: country.IsRegistrationEnabled });
          }
        } else {
          if (responses.length > 2) {
            LoggerProvider.info(this, 'InitializeResources: extended response');
            if (this.calculator){
              this.sendCountries = this.calculator.availableSendToCountries;
            }
            this.destinationCountries = responses[2];

            this.countries = [];
            const hasCountries = responses && responses[3] && responses[3].length > 0;
            this.allCountries = hasCountries ? responses[3] as Country[] : [];// Reverted the code added to avoid build issue and added the fix from beta.
            this.allCountries = this.allCountries.sort(function (a, b) {
              return a.Name.toUpperCase() > b.Name.toUpperCase() ? 1 : b.Name.toUpperCase() > a.Name.toUpperCase() ? -1 : 0;
            });

            const favoriteCountries = [];
            if (this.calculator.fields) {
              const field = this.calculator.fields.find((field) => field.id === 'topCorridors');
              if (field && field.options) {
                for (const option of field.options) {
                  favoriteCountries.push(option.value);
                }
              }
            }
            const sendCountries = [];
            for (const sendCountry of this.sendCountries) {
              sendCountries.push(sendCountry.value);
            }
            const destinationCountries = {};
            for (const destinationCountry of this.destinationCountries) {
              destinationCountries[destinationCountry.NameAbbrev] = destinationCountry;
            }

            for (let i=0; i < this.allCountries.length; i++) {
              const country: any = this.allCountries[i];
              const countryValue = country.NameAbbrev.toUpperCase();
              const destinationCountry = destinationCountries[countryValue]
              if (destinationCountry) {
                Object.assign(destinationCountry, country); // first apply country to destination country since some values are missing destination country - so to ensure they don't get overwritten
                Object.assign(country, destinationCountry); // then apply destination country to get the additional properties
                country.IsOfferedDestinationCountry = true;
              } else
                country.IsOfferedDestinationCountry = false;

              country.IsRegistrationCountry = (registrationCountries.indexOf(countryValue) >= 0);
              country.IsSendToCountry = (sendCountries.indexOf(countryValue) >= 0);
              country.IsFavoriteSendToCountry = (favoriteCountries.indexOf(countryValue) >= 0);
              country.PhoneExtension = country.CountryCode.trim();
              
              this.countries.push({ value: countryValue, text: country.Name, countryCode: country.CountryCode.trim() });
            }

            this.resources.Countries = this.allCountries;
          }
        }

        LoggerProvider.info(this, 'InitializeResources: resolved request ' + requestNr);
        this.initResolve();
        this.initResolve = null;
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to process initialize resources result')
        // initPromise was already cleared above; release waiters like the error
        // handler below does, so callers (e.g. the Vue bridge fetch-resources
        // handler) don't await a promise that can no longer be resolved.
        if (this.initResolve) {
          this.initResolve();
          this.initResolve = null;
        }
        window.setTimeout(() => {
          this.initializeResources(true, forceCountryReload);
        }, 10000);
      }
      return true;
    }, (err) => {
      if (requestNr !== this.requestNr) {
        LoggerProvider.info(this, 'InitializeResources: exception for outdated request ' + requestNr, err);
        return;
      }
      else if (err.status === 401 || err.status === 403) { // these are already handled and shouldn't cause a reload
        LoggerProvider.info(this, 'InitializeResources: 401/403 exception');
        if (this.initResolve) {
          this.initResolve();
          this.initResolve = null;
        }
        this.initPromise = null;
      }
      else {
        LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'InitializeResources: exception for request ' + requestNr);
        if (this.initResolve) {
          this.initResolve();
          this.initResolve = null;
        }
        this.initPromise = null;
        window.setTimeout(() => {
          this.initializeResources(true, forceCountryReload);
        }, 10000);
      }
    });
  }


  paymentMethodTextAndLabel(method: any) {
    let methodObj: any = {};
    
    switch (method.value) {
      case PaymentMethodType.PayNearMe:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.payNearMe');
        methodObj.subTitle = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.cashTimeLabel');
        break;
      case PaymentMethodType.StagedRia:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.stagedRia');
        methodObj.subTitle = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.cashTimeLabel');
        break;
      case PaymentMethodType.BankAccount:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.bankAccount');
        if(APP_CONFIG.region.defaultCountryFrom === 'CA') {
          methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.directDebit_CA');
        }
        methodObj.subTitle = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.bankAccountTimeLabel');
        break;
      case PaymentMethodType.CreditCard:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.creditCard');
        methodObj.subTitle = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.creditDebitTimeLabel');
        break;
      case PaymentMethodType.DebitCard:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.debitCard');
        methodObj.subTitle = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.creditDebitTimeLabel');
        break;
      case PaymentMethodType.SencillitoOnline:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.sencillito');
        break;
      case PaymentMethodType.DirectBank:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.directBank');
        // IMPORTANT: that applies to several payment methods so we would need individual texts here
        break;
      case PaymentMethodType.Giropay:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.directBank_Giropay');
        break;
      case PaymentMethodType.PayID:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.payID');
        methodObj.subTitle = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.payIDTimeLabel');
        break;
      case PaymentMethodType.RiaWallet:
        methodObj.text = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.RiaMobilePayment');
        methodObj.subTitle = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.walletTimeLabel');
        break;
      case PaymentMethodType.StagedOnline:
        methodObj.text = this.translate.instant('page.sendReceiptPage.paymentMethods.payNow');
        break;
      case PaymentMethodType.Cash:
        methodObj.subTitle = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.cashTimeLabel');
        break;
      case PaymentMethodType.ApplePay:
        if (APP_CONFIG.region.adyenMerchantCapability.indexOf('credit') > -1) {
          methodObj.text = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.applePay');
        } else {
          methodObj.text = this.translate.instant('form.moneyTransferCalculatorForm.paymentMethods.optionsList.applePayDebitOnly');
        }
        break;
      case PaymentMethodType.GooglePay:
        if (APP_CONFIG.region.adyenMerchantCapability.indexOf('credit') > -1) {
          methodObj.text = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.googlePay');
        } else {
          methodObj.text = this.translate.instant('form.moneyTransferCalculatorForm.paymentMethods.optionsList.googlePayDebitOnly');
        }
        break;
      case PaymentMethodType.GooglePayCredit:
         methodObj.text = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.googlePay');
        break;
      case PaymentMethodType.ApplePayCredit:
         methodObj.text = this.translate.instant('page.paymentAccountDetailsPage.paymentMethods.applePay');
        break;
      default:
        // Handle any other cases if necessary
        break;
    }

    const data = this.getPaymentMethodData(method.value);
    if (data && data.title) {
      methodObj.text = data.title;
    }

    return methodObj;
  }

  ensureInitializedResources(forceReload: boolean = false) {
    this.initializeResources(forceReload);
    if (this.initPromise)
      return this.initPromise;
    return null;
  }

  isPayInStoreEnabled(): boolean {
    return Array.isArray(this.paymentMethods)
      && this.paymentMethods.some((method) => method && method.value === PaymentMethodType.StagedRia);
  }

  async getResources(forceReload: boolean = false) {
    await this.ensureInitializedResources(forceReload);
    return this.resources;
  }

  // Refresh ONLY the api/Resources FeaturesTask (country features/settings) in place mirrors initializeResources, 
  // without the Calculator/Initialize + Agents/PaymentMethods calls. 
  async refreshFeaturesTask(countryId: number = APP_CONFIG.region.regionId): Promise<void> {
    if (!this.useResourcesEndpoint) {
      return;
    }
    const cacheParam = ResourceProvider.cleanCache ? '&cleanCache=true' : '';
    const resources: any = await this.httpProvider.get(`${APP_CONFIG.API_URL}api/Resources?CountryId=${countryId}&locale=${HttpProvider.currentLanguage}${cacheParam}`);
    if (resources && resources.FeaturesTask) {
      this.resources.FeaturesTask = resources.FeaturesTask;
    }
  }

  async getPriceCalculator(): Promise<IInitializePriceCalculator> {
    await this.ensureInitializedResources();
    return this.calculator;
  }

  async getAllCountries(): Promise<Country[]> {
    await this.ensureInitializedResources();
    return this.allCountries;
  }

  async getCountries(): Promise<IDictionary[]> {
    await this.ensureInitializedResources();
    return this.countries;
  }

  async getUserCountry() {
    const countries = await this.getCountries();
    const country = countries.find(country => {
      return country.value === APP_CONFIG.region.defaultCountryFrom;
    });
    return country;
  }

  async getTopCountries(): Promise<IDictionary[]> {
    await this.ensureInitializedResources();
    if (this.calculator.fields) {
      const field = this.calculator.fields.find((field) => field.id === 'topCorridors');
      if (field)
        return field.options;
    }
    return null;
  }  

  getPspPaymentMethod(paymentMethod) {
    if (!this.paymentMethods)
      return null;
    const method = this.paymentMethods.find((m) => (m.value === paymentMethod));
    if (method)
      return method.pspPaymentMethod;
    return null;
  }

  async getPaymentMethods() {
    await this.ensureInitializedResources();    
    return this.paymentMethods;
  }

  async getSendCountries() {
    await this.ensureInitializedResources();    
    return this.sendCountries;
  }

  async getDestinationCountries() {
    await this.ensureInitializedResources();    
    return this.destinationCountries;
  }

  async getCountryByValue(countryValue: string): Promise<ICountry> {
    return new Promise<ICountry>(async (resolve, reject) => {
      await this.ensureInitializedResources();
      const country = this.destinationCountries.find(country => country.NameAbbrev === countryValue);
      let countryRes;
      if(country) {
        countryRes = {
          isoCode: country.NameAbbrev,
          countryName: country.Name,
          isOpenPaymentCountry: country.IsOpenPaymentCountry ? country.IsOpenPaymentCountry : country.OpenPayment,
          countryId: country.Id
        }
      }
      else countryRes = null;
      resolve(countryRes);
    });
  }

  async getCountryByName(countryCode: string): Promise<ICountry> {
    return new Promise<ICountry>(async (resolve, reject) => {
      await this.ensureInitializedResources();
      const country = this.countries.find(country => country.value === countryCode);
      let countryRes;
      if(country) {
        countryRes = {
          countryName: country.text,
          countryId: country.value
        }
      }
      else countryRes = null;
      resolve(countryRes);
    });
  }

  async getCountryByNameAbbrev(NameAbbrev: string) {
    const countries = await this.getAllCountries();
    for (let country of countries) {
      if (country.NameAbbrev == NameAbbrev) {
        return country;
      }
    }
    return undefined;
  }

  async getCountryById(id: number) {
    const countries = await this.getAllCountries();
    for (let country of countries) {
      if (country.Id == id) {
        return country;
      }
    }
    return undefined;
  }

  async getCountryCountryCode(countryCode: string) {
    const countries = await this.getAllCountries();

    if(countryCode[0] == '+')
      countryCode = countryCode.slice(1);
    for(let i = countryCode.length; i < 3 ; i++){
      countryCode = countryCode.concat(" ");
    }
    for (let country of countries) {
      if (country.CountryCode == countryCode) {
        return country;
      }
    }
    return undefined;
  }  

  getDirectBankBrand() {
    this.directBankBrand = APP_CONFIG.region.directBankBrand ? APP_CONFIG.region.directBankBrand : '';
    return this.directBankBrand;
  }

  getPayNearMeBrand(force = false) {
    if (!APP_CONFIG.featureFlags.kroger && !force) return '';
    try {
      return (APP_CONFIG.region.payNearMeBrand && APP_CONFIG.region.payNearMeBrand != '') ? APP_CONFIG.region.payNearMeBrand:'';
    } catch (ex) {
      LoggerProvider.exception(this,ex,LoggerEvent.ProviderException,"cant get paynearme brand")
    }
  }

  getPaymentMethodData(type, payNearMeBrand = '', stagedRiaBrand = '') {
    const data = {
      name: type,
      title: '',
      image: 'assets/icons/cash_pickup_delivery.svg',
      imageCircle: true,
      pspAutoSubmit: false,
      pspTotalInFooter: false,
      pspInlineContainer: false,
      pspInlineLoading: false,
      pspSeparateContainer: false,
      pspSeparateContainerHeader: '',
      pspChangeAvailable: false,
      pspHelperText: '',
      pspAlertType: 'info',
      pspAlertHeader: '',
      pspAlertText: '',
      pspErrors: {},
      useAdditionalFlow: false,
    };

    if (typeof(this.directBankBrand) === 'undefined')
      this.getDirectBankBrand();

    if (type === PaymentMethodType.BankAccount) {
      data.image = 'assets/icons/bank_delivery.svg';
    } else if (type === PaymentMethodType.CreditCard) {
      data.image = 'assets/psp/credit-card.svg';
      data.pspChangeAvailable = true;
    } else if (type === PaymentMethodType.DebitCard) {
      data.image = 'assets/psp/credit-card.svg';
      data.pspChangeAvailable = true;
    } else if (type === PaymentMethodType.PayNearMe) {
      data.image = 'assets/icons/cash_pickup_delivery.svg';
    } else if (type === PaymentMethodType.Cash) {
      data.image = 'assets/icons/cash_pickup_delivery.svg';
    } else if (type === PaymentMethodType.StagedRia) {
      data.image = 'assets/icons/cash_pickup_delivery.svg';
    } else if (type === PaymentMethodType.DirectBank) {
      data.image = 'assets/icons/direct_bank.svg';
    } else if (type === PaymentMethodType.RiaWallet) {
      data.image = 'assets/icons/eWallet-smf.svg';
    } else if (type === PaymentMethodType.Giropay) {
      data.image = 'assets/icons/giropay.svg';
    } else if (type === PaymentMethodType.StagedOnline) {
      data.image = 'assets/icons/pay_now.svg';
    } else if (type === PaymentMethodType.GooglePay) {
      data.image = 'assets/icons/trademarked/google-pay.svg';
      data.imageCircle = false;
    } else if (type === PaymentMethodType.ApplePay) {
      data.image = 'assets/icons/trademarked/apple-pay.svg';
      data.imageCircle = false;
    } else if (type === PaymentMethodType.GooglePayCredit) {
      data.image = 'assets/icons/trademarked/google-pay.svg';
      data.imageCircle = false;
    } else if (type === PaymentMethodType.ApplePayCredit) {
      data.image = 'assets/icons/trademarked/apple-pay.svg';
      data.imageCircle = false;
    } else if (type === PaymentMethodType.OpenBanking) {
      data.image = 'assets/pay-by-bank/pay-by-bank-logo.svg';
      data.imageCircle = false;
      data.title = this.translate.instant('page.calculator.truelayer.body');
    }

    if (type === PaymentMethodType.DirectBank) {
      if (this.directBankBrand === DirectBankBrand.Klarna) {
         if (APP_CONFIG.region.defaultCountryFrom == 'DE' || APP_CONFIG.region.defaultCountryFrom == 'AT' || APP_CONFIG.region.defaultCountryFrom == 'ES') {
          data.image = 'assets/icons/klarna.svg';
          data.title = this.translate.instant('page.sendReceiptPage.paymentMethods.directBank_DE');
          data.imageCircle = false;
          data.pspTotalInFooter = true;
          data.pspAlertType = 'notice';
          data.pspAlertHeader = this.translate.instant('page.paymentMethods.klarnaRedirection.header');
          data.pspAlertText = this.translate.instant('page.paymentMethods.klarnaRedirection.description');
          data.useAdditionalFlow = false;
        }
        else data.image = 'assets/icons/sofort.svg';
      } else if(this.directBankBrand == DirectBankBrand.Fintoc ){
        if (APP_CONFIG.region.defaultCountryFrom == 'CL') data.image = 'assets/icons/bank_delivery.svg';
      } else if (this.directBankBrand === DirectBankBrand.FPX) {
        data.image = 'assets/icons/fpx.svg';
      } else if (this.directBankBrand === DirectBankBrand.IDeal) {
        data.image = 'assets/icons/ideal.svg';
        data.title = 'iDEAL | Wero';
        data.imageCircle = false;
        data.useAdditionalFlow = false;
      } else if (this.directBankBrand === DirectBankBrand.Bancontact) {
        data.image = 'assets/icons/bancontact_pay.svg';
        data.imageCircle = false;
        data.pspChangeAvailable = true;
        data.title = 'Bancontact Pay';
        data.pspAlertType = 'notice';
        data.pspAlertHeader = this.translate.instant('page.paymentMethods.redirection.header') + ' ' + data.title;
        data.pspAlertText = this.translate.instant('page.paymentMethods.bancontactPayRedirection.description');
        data.pspAutoSubmit = true;
        data.pspTotalInFooter = true;
        data.pspInlineContainer = true;
        data.useAdditionalFlow = false;
      } else if (this.directBankBrand === DirectBankBrand.Blik) {
        data.image = 'assets/icons/blik.svg'
        data.title = 'BLIK';
        data.imageCircle = false;
        data.pspChangeAvailable = true;
        data.pspSeparateContainerHeader = this.translate.instant('page.paymentMethods.blikRedirection.containerHeader');
        data.pspAlertType = 'notice';
        data.pspAlertHeader = this.translate.instant('page.paymentMethods.blikRedirection.header');
        data.pspAlertText = this.translate.instant('page.paymentMethods.blikRedirection.description');
        data.pspHelperText = this.translate.instant('page.paymentMethods.blikRedirection.helperText');
        data.pspTotalInFooter = true;
        data.pspInlineLoading = true;
        data.useAdditionalFlow = true;
        data.pspErrors = {
          'ApmAuthorizationFailed': {
            'error': this.translate.instant('page.paymentMethods.paymentDeclinedTitle'),
            'errorTitle': this.translate.instant('page.paymentMethods.paymentDeclinedBody')
          }
        }
      } else if (this.directBankBrand === DirectBankBrand.MBWay) {
        data.image = 'assets/icons/mbway.svg';
        data.title = 'MB WAY';
        data.imageCircle = false;
        data.pspAlertType = 'notice';
        data.pspAlertHeader = this.translate.instant('page.paymentMethods.blikRedirection.header');
        data.pspAlertText = this.translate.instant('page.paymentMethods.MBWayPaymentConfirmation.description');
        data.pspTotalInFooter = true;
        data.pspInlineLoading = true;
        data.useAdditionalFlow = true;
      } else if(this.directBankBrand === DirectBankBrand.Swish) {
        data.image = 'assets/icons/swish.svg';
        data.title = 'Swish';
        data.imageCircle = false;
        data.pspAlertType = 'notice';
        data.pspChangeAvailable = true;
        data.pspAlertHeader = this.translate.instant('page.paymentMethods.swish.redirecting.title');
        data.pspAlertText = this.translate.instant('page.paymentMethods.swish.redirecting.body');
        data.pspAutoSubmit = true;
        data.pspTotalInFooter = true;
        data.pspInlineContainer = true;
        data.useAdditionalFlow = false;
      }

    } else if (type === PaymentMethodType.StagedRia) {
      if (APP_CONFIG.featureFlags.kroger && stagedRiaBrand === StagedRiaBrand.Kroger) data.image = 'assets/icons/kroger.svg';
    }
    else if (type === PaymentMethodType.PayNearMe) {
      if (!payNearMeBrand)
        payNearMeBrand = this.getPayNearMeBrand(true); // TEMPORARY !!!!!!!!!!!!!!!!  
      if (payNearMeBrand === PayNearMeBrand.SevenEleven) data.image = 'assets/icons/sevenEleven.svg';
    }
    return data;
  }  
}



// WEBPACK FOOTER //
// ./src/providers/resource/resource.ts